/**
 * The build dock (B0.2's palette, restated at M1.1 as a bottom-left dock):
 * two large mode buttons — **Inspect** and **Build** — and a shelf that
 * expands in build mode with the rooms and machines the catalog offers, plus
 * demolish and move. Inspect mode's shelf is the selected thing's facts.
 *
 * Everything here is data. The room and tool lists come from the catalog Rust
 * handed across at boot, priced from the same numbers the sim charges; the
 * inspect panel prints the facts the sim published for the selected thing
 * rather than knowing what a furnace is; and a refusal is the sentence the sim
 * wrote. React's job in this file is layout and clicks, and nothing else.
 * The layout file buttons moved into the settings gear.
 */

import { useState, useSyncExternalStore } from 'react';
import type { FloorHost, MachineUi, SiteCatalogUi, ToolModelUi, UsedListingUi } from '../bridge';
import { buildState, setBuild, subscribeBuild, type BuildMode } from './build';
import { snapshot, subscribe, type HudState } from './store';
import { openHandbook, toggleEconomy, usePanels } from './panels';
import { ViewStrip, type FlyTo } from './SiteFlow';

type Shelf = 'tools' | 'rooms';

export function BuildPalette({ host, flyTo }: { host: FloorHost; flyTo: FlyTo }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const build = useSyncExternalStore(subscribeBuild, buildState);
  const panels = usePanels();
  const [shelf, setShelf] = useState<Shelf>('tools');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [showUsed, setShowUsed] = useState(false);
  const catalog = hud.catalog;
  if (!catalog) return null;

  const mode = build.mode;
  const building = mode.kind !== 'inspect';
  const pick = (next: BuildMode) => setBuild({ mode: next, anchor: null, verdict: null });
  const enterBuild = () => {
    if (!building) pick({ kind: 'build' });
  };

  return (
    <div className={`dock ${building ? 'building' : ''}`}>
      <div className="dock-modes-wrap">
      <div className="dock-modes">
        <button
          className={`dock-mode ${!building ? 'active' : ''}`}
          onClick={() => pick({ kind: 'inspect' })}
          title="inspect: click anything on the floor for its numbers"
        >
          <span className="dock-icon">🔍</span>
          <span className="dock-label">Inspect</span>
        </button>
        <button
          className={`dock-mode ${building ? 'active' : ''}`}
          onClick={enterBuild}
          title="build: rooms, machines, demolish and move"
        >
          <span className="dock-icon">🏗</span>
          <span className="dock-label">Build</span>
        </button>
        <button
          className={`dock-mode ${panels.economy === 'line' ? 'active' : ''}`}
          onClick={() => toggleEconomy('line')}
          title="your fab: its readings, what the last move bought, and the levers"
        >
          <span className="dock-icon">🏭</span>
          <span className="dock-label">Your fab</span>
        </button>
      </div>
      <ViewStrip host={host} flyTo={flyTo} />
      </div>

      {building ? (
        <div className="dock-shelf">
          <div className="dock-tabs">
            <button className={shelf === 'tools' ? 'active' : ''} onClick={() => setShelf('tools')}>
              Machines
            </button>
            <button className={shelf === 'rooms' ? 'active' : ''} onClick={() => setShelf('rooms')}>
              Rooms
            </button>
            <span className="dock-sep" />
            <button
              className={mode.kind === 'bulldoze' ? 'active' : ''}
              onClick={() => pick({ kind: 'bulldoze' })}
              title="demolish: click a room or a machine"
            >
              Demolish
            </button>
            <button
              className={mode.kind === 'relocate' ? 'active' : ''}
              onClick={() => pick({ kind: 'relocate' })}
              title="move: click a machine, then click where it should stand — it keeps its queue and its history"
            >
              Move
            </button>
            <span className="hint">esc cancels · r rotates · {catalog.era}</span>
          </div>
          {shelf === 'tools' ? (
            <MachineShelf
              state={hud}
              catalog={catalog}
              mode={mode}
              pick={pick}
              typeId={typeId}
              setTypeId={setTypeId}
              showUsed={showUsed}
              setShowUsed={setShowUsed}
            />
          ) : (
          <div className="dock-tiles">
            {catalog.rooms.map((room) => (
                  <button
                    key={room.id}
                    className={`tile ${mode.kind === 'room' && mode.typeId === room.id ? 'active' : ''}`}
                    onClick={() => pick({ kind: 'room', typeId: room.id })}
                    title={`${room.name} — ${room.zone} zone · ${room.minW}×${room.minH} to ${room.maxW}×${room.maxH} cells${
                      room.connective ? ' · walk through' : ''
                    }${room.entry ? ' · must touch the plot edge' : ''}${
                      room.utilitySupplyPerCell > 0 ? ` · supplies ${room.utilitySupplyPerCell}/cell` : ''
                    }${room.utilityPerCell > 0 ? ` · draws ${room.utilityPerCell}/cell` : ''} — drag out a footprint`}
                  >
                    <span className="tile-swatch" style={{ background: room.color }} />
                    <span className="tile-name">{room.name}</span>
                    <span className="tile-cost">${room.costPerCellDollars.toLocaleString()}/cell</span>
                    <span className="tile-note">{room.zone} zone</span>
                  </button>
                ))}
          </div>
          )}
          <Refusal state={hud} />
        </div>
      ) : (
        <div className="dock-shelf inspect-shelf">
          <Selection state={hud} />
          <Machine state={hud} host={host} />
          <Refusal state={hud} />
        </div>
      )}
    </div>
  );
}

/**
 * The machines shelf (M1.2, laid out as a catalogue): one tab per tool
 * *type* — the thing a recipe names — and a table of the models answering
 * it, every stat that differs in its own column, because the trade-offs are
 * the decision and a tile could not hold them. The **used** checkbox folds
 * the second-hand market's listings for the same type into the table as
 * extra rows: same machine, the cycle's price, condition unknown until
 * qualified. A row arms the same build cursor a tile did; a used row routes
 * the click through the campaign so the market stamps the price.
 */
function MachineShelf({
  state,
  catalog,
  mode,
  pick,
  typeId,
  setTypeId,
  showUsed,
  setShowUsed,
}: {
  state: HudState;
  catalog: SiteCatalogUi;
  mode: BuildMode;
  pick: (next: BuildMode) => void;
  typeId: string | null;
  setTypeId: (id: string) => void;
  showUsed: boolean;
  setShowUsed: (on: boolean) => void;
}) {
  const type = catalog.tools.find((tool) => tool.id === typeId) ?? catalog.tools[0];
  if (type == null) return null;
  const market = state.campaign?.usedMarket;
  const listings = showUsed && market != null ? market.listings.filter((l) => l.toolId === type.id) : [];
  const arm = (modelId: string, used: boolean) =>
    pick({ kind: 'tool', typeId: type.id, modelId, ...(used ? { used: true } : {}) });
  const armed = (modelId: string, used: boolean) =>
    mode.kind === 'tool' && mode.typeId === type.id && mode.modelId === modelId && !!mode.used === used;
  return (
    <>
      <div className="dock-type-tabs">
        {catalog.tools.map((tool) => (
          <button
            key={tool.id}
            className={type.id === tool.id ? 'active' : ''}
            onClick={() => setTypeId(tool.id)}
            title={`${tool.name} — ${tool.w}×${tool.h} cells · ${
              tool.batchWafers > 1 ? `batches of ${tool.batchWafers}` : 'single wafer'
            } · draws ${tool.utility} utility${tool.attended ? ' · worked by hand' : ''}`}
          >
            <span className="dot" style={{ background: tool.color }} />
            {tool.name}
          </button>
        ))}
        <label
          className="used-toggle"
          title="fold the second-hand market's listings into the table: the cycle's price, no vendor spares, condition unknown until installed and qualified"
        >
          <input type="checkbox" checked={showUsed} onChange={(e) => setShowUsed(e.target.checked)} />
          used
          {showUsed && market != null ? (
            <span>
              · {market.supplyFabs.toFixed(1)} fabs&apos; worth at {Math.round(market.priceMille / 10)}% of list
            </span>
          ) : null}
          <button
            className="link"
            onClick={(e) => {
              // Inside the label: without this the click also toggles the box.
              e.preventDefault();
              e.stopPropagation();
              openHandbook('used-market');
            }}
          >
            ?
          </button>
        </label>
      </div>
      <div className="dock-table-wrap">
        <table className="dock-table">
          <thead>
            <tr>
              <th>model</th>
              <th>price</th>
              <th>wafers/h</th>
              <th>setup</th>
              <th>fails</th>
              <th>repair</th>
              <th>D₀</th>
              <th>cap.</th>
              <th>upkeep/wk</th>
              <th>install</th>
              <th>{showUsed ? 'on market' : ''}</th>
            </tr>
          </thead>
          <tbody>
            {type.models.map((model) => (
              <ModelRow
                key={model.id}
                model={model}
                active={armed(model.id, false)}
                onClick={() => arm(model.id, false)}
              />
            ))}
            {listings.map((listing) => {
              const model = type.models.find((m) => m.id === listing.modelId);
              if (model == null) return null;
              return (
                <ModelRow
                  key={`${listing.modelId}-used`}
                  model={model}
                  listing={listing}
                  active={armed(listing.modelId, true)}
                  onClick={() => arm(listing.modelId, true)}
                />
              );
            })}
            {showUsed && listings.length === 0 ? (
              <tr className="hint-row">
                <td colSpan={11}>
                  no used {type.name.toLowerCase()} on the market — supply arrives when somebody&apos;s fab is
                  liquidated, retooled or idled by a glut
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * One machine on offer. With a `listing` it is the same model second-hand:
 * the market's price against list, double qualification, and the columns the
 * buyer cannot know struck through to "?" — condition is revealed only after
 * install and qualification, which is the whole gamble.
 */
function ModelRow({
  model,
  listing,
  active,
  onClick,
}: {
  model: ToolModelUi;
  listing?: UsedListingUi;
  active: boolean;
  onClick: () => void;
}) {
  const used = listing != null;
  const title = used
    ? `${model.name} (${model.vendor}), used — $${Math.round(listing.priceDollars / 1000)}k against $${Math.round(
        listing.listDollars / 1000,
      )}k list · condition unknown until installed and qualified (${model.installWeeks}+${
        model.qualifyWeeks * 2
      } weeks) · no vendor spares: repairs take half again as long`
    : `${model.name} (${model.vendor}) — ${model.installWeeks}+${model.qualifyWeeks} weeks to install and qualify`;
  return (
    <tr className={`${active ? 'active' : ''} ${used ? 'used-row' : ''}`} onClick={onClick} title={title}>
      <td>
        {model.name}
        {used ? <span className="vendor"> · used</span> : null}
      </td>
      <td className="price">${Math.round((used ? listing.priceDollars : model.costDollars) / 1000)}k</td>
      <td>{model.wafersPerHour}</td>
      <td>{model.setupSeconds}s</td>
      <td>{model.mtbfHours > 0 ? `${model.mtbfHours}h` : '—'}</td>
      <td>
        {model.mtbfHours > 0 ? `${model.repairMinutes}m` : '—'}
        {used && model.mtbfHours > 0 ? '×1.5' : ''}
      </td>
      <td>{used ? '?' : `×${(model.d0Mille / 1000).toFixed(2)}`}</td>
      <td>×{(model.capabilityMille / 1000).toFixed(2)}</td>
      <td>${Math.round(model.upkeepDollars).toLocaleString()}</td>
      <td>
        {model.installWeeks}+{used ? model.qualifyWeeks * 2 : model.qualifyWeeks}w
      </td>
      <td>{used ? `${listing.units} unit${listing.units === 1 ? '' : 's'}` : ''}</td>
    </tr>
  );
}

/**
 * The selected machine as a *machine* (M1.2): its condition, its status, its
 * provenance, and the three levers that act on it — keep it up or neglect it,
 * strip it down, or sell it into the used market at the price the market
 * names.
 */
function Machine({ state, host }: { state: HudState; host: FloorHost }) {
  const selection = state.ui?.site.selection;
  if (!selection || selection.kind !== 'tool') return null;
  const machine: MachineUi | undefined = state.ui?.fleet.machines.find((m) => m.id === selection.id);
  if (machine == null) return null;
  const market = state.campaign?.usedMarket;
  return (
    <div className="inspect machine">
      <div className="inspect-facts">
        <div className="row">
          <span className="label">machine</span>
          <span className="value">
            {machine.status}
            {machine.weeksLeft > 0 ? ` — ${machine.weeksLeft.toFixed(1)} wk left` : ''}
          </span>
        </div>
        <div className="row">
          <span className="label">condition</span>
          <span className="value">
            {machine.revealed
              ? `${machine.band} (${Math.round(machine.conditionMille / 10)}%)`
              : 'unknown until qualified'}
          </span>
        </div>
        {machine.mtbfHours > 0 ? (
          <div className="row">
            <span className="label">fails</span>
            <span className="value">
              ~every {machine.mtbfHours} h · {machine.repairMinutes} min
            </span>
          </div>
        ) : null}
        <div className="row">
          <span className="label">paid</span>
          <span className="value">
            ${Math.round(machine.priceDollars / 1000)}k{machine.used ? ' · used' : ''}
          </span>
        </div>
        {machine.overhauls > 0 ? (
          <div className="row">
            <span className="label">overhauls</span>
            <span className="value">
              {machine.overhauls} · ${Math.round(machine.overhaulDollars / 1000)}k
            </span>
          </div>
        ) : null}
      </div>
      <div className="machine-levers">
        <button
          onClick={() => host.send({ kind: 'setMaintenance', id: machine.id, maintained: !machine.maintained })}
          title={
            machine.maintained
              ? 'stop paying routine maintenance: cheaper by the week, wears three times as fast and breaks more'
              : 'resume routine maintenance: the full upkeep bill, and the machine stops racing to the grave'
          }
        >
          {machine.maintained ? 'Neglect' : 'Keep up'}
        </button>
        <button
          disabled={!machine.inService || machine.busy}
          onClick={() => host.send({ kind: 'overhaul', id: machine.id })}
          title={`strip it down and rebuild it as new: $${Math.round(
            machine.overhaulCostDollars / 1000,
          )}k and ${machine.overhaulWeeks} weeks offline${machine.busy ? ' — waiting for its run to finish' : ''}`}
        >
          Overhaul ${Math.round(machine.overhaulCostDollars / 1000)}k
        </button>
        <button
          disabled={machine.busy || market == null}
          onClick={() => host.sendCampaign({ kind: 'sellUsed', id: machine.id })}
          title="sell it into the second-hand market at the price the market names — its list, at the market's rate, scaled by its condition"
        >
          Sell used
        </button>
      </div>
    </div>
  );
}

/** The last refused edit, in the sim's own words. */
function Refusal({ state }: { state: HudState }) {
  const refusal = state.ui?.site.refusal;
  if (!refusal) return null;
  return (
    <div className="refusal">
      <strong>Refused</strong> <span className="code">{refusal.code}</span>
      <p>{refusal.message}</p>
    </div>
  );
}

/** What the inspect cursor is on, with the numbers the sim published for it. */
function Selection({ state }: { state: HudState }) {
  const selection = state.ui?.site.selection;
  if (!selection) return <span className="hint">click a room, a machine or a lot for its numbers</span>;
  return (
    <div className="inspect">
      <h4>
        {selection.name}
        <span className="muted">
          {' '}
          at {selection.x},{selection.y}
        </span>
      </h4>
      <div className="inspect-facts">
        {selection.facts.map((fact, index) => (
          <div className="row" key={`${fact.label}-${index}`}>
            <span className="label">{fact.label}</span>
            <span className="value">{fact.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
